import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Eye, EyeOff } from 'lucide-react'
import { isAxiosError } from 'axios'
import { getMe, login, loginVerify, sendLoginEmailCode, type LoginSuccess, type TwoFactorChallenge } from '@/lib/auth'
import { useAuth } from '@/contexts/AuthContext'
import { getApiErrorMessage } from '@/lib/api-error'

export function LoginPage() {
  const navigate = useNavigate()
  const { setUser } = useAuth()
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [challenge, setChallenge] = useState<TwoFactorChallenge | null>(null)
  const [method, setMethod] = useState<'TOTP' | 'EMAIL' | 'RECOVERY'>('TOTP')
  const [code, setCode] = useState('')
  const [trustDevice, setTrustDevice] = useState(false)
  const [emailSent, setEmailSent] = useState(false)
  const [sendingEmail, setSendingEmail] = useState(false)

  // Odpoveď loginu nesie len základ používateľa (bez nastavení kancelárie, kreditov a profilu) — celého vráti
  // /auth/me ako po obnovení stránky; bez neho by napr. tiper nevidel pole Agent, kým stránku neobnoví.
  async function enter(result: LoginSuccess) {
    setUser(await getMe().catch(() => result.user))
    navigate(result.mustChangePassword ? '/change-password' : '/dashboard', { replace: true })
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)
    setLoading(true)

    const form = e.currentTarget
    const email = (form.elements.namedItem('email') as HTMLInputElement).value
    const password = (form.elements.namedItem('password') as HTMLInputElement).value

    try {
      const result = await login(email, password)
      if ('twoFactorRequired' in result) {
        setChallenge(result)
        setMethod(result.defaultMethod)
        return
      }
      await enter(result)
    } catch (err) {
      // 401 = one shared text for unknown e-mail / wrong password / temporary lock, 403 = deactivated
      // account or inactive subscription. 400 (ValidationPipe: e-mail format, password too short)
      // is a wrong password for the user too.
      const invalid = 'Nesprávny e-mail alebo heslo.'
      setError(isAxiosError(err) && err.response?.status === 400 ? invalid : getApiErrorMessage(err, invalid))
    } finally {
      setLoading(false)
    }
  }

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault()
    if (!challenge) return
    setError(null); setLoading(true)
    try {
      await enter(await loginVerify(challenge.challenge, method, code.trim(), trustDevice))
    } catch (err) {
      setError(getApiErrorMessage(err, 'Neplatný kód. Skúste znova.'))
    } finally { setLoading(false) }
  }

  async function handleSendEmail() {
    if (!challenge || sendingEmail) return
    setError(null); setSendingEmail(true)
    try { await sendLoginEmailCode(challenge.challenge); setEmailSent(true) }
    catch { setError('Kód sa nepodarilo odoslať.') }
    finally { setSendingEmail(false) }
  }

  // Prepnutie metódy v 2FA kroku — zahodí predošlú chybu aj zadaný kód,
  // aby sa pod novou metódou nezobrazovala chyba/kód z predošlého pokusu.
  function switchMethod(next: 'TOTP' | 'EMAIL' | 'RECOVERY') {
    setMethod(next); setError(null); setCode(''); setEmailSent(false)
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#050b1f] text-slate-100">
      {/* Ambient glow — peaks at #0358a3 */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(70% 60% at 20% 25%, #0358a3 0%, rgba(3,88,163,0.55) 35%, rgba(3,88,163,0) 70%), radial-gradient(60% 55% at 85% 80%, rgba(3,88,163,0.85) 0%, rgba(3,88,163,0.3) 45%, rgba(3,88,163,0) 75%), radial-gradient(45% 40% at 50% 110%, rgba(14,165,233,0.35) 0%, rgba(2,6,23,0) 75%)',
        }}
      />

      <div className="relative mx-auto flex min-h-screen max-w-6xl flex-col items-stretch gap-0 px-4 py-4 sm:px-6 sm:py-10 sm:gap-4 lg:flex-row lg:items-center lg:gap-16 lg:px-12">
        {/* Left — logo only */}
        <div className="flex items-center justify-center -mb-8 sm:-mb-12 lg:mb-0 lg:flex-1 lg:pr-6">
          <img
            src="/realnexa-logo.png"
            alt="Realnexa"
            className="h-64 sm:h-80 lg:h-[32rem] 3xl:h-[40rem] w-auto lg:max-h-[80vh] object-contain drop-shadow-[0_8px_40px_rgba(56,189,248,0.35)]"
          />
        </div>

        {/* Right — login card */}
        <div className="mx-auto flex w-full max-w-md shrink-0 flex-col justify-center lg:mx-0 lg:w-[420px]">
          <div className="relative">
            {/* Outer blue halo glow */}
            <div
              aria-hidden
              className="pointer-events-none absolute -inset-6 rounded-[28px] bg-sky-500/25 blur-3xl"
            />
            <div
              aria-hidden
              className="pointer-events-none absolute -inset-2 rounded-3xl bg-gradient-to-br from-sky-400/40 via-blue-500/30 to-sky-600/40 blur-xl"
            />

            <div className="relative rounded-2xl border border-sky-400/25 bg-[#0a1530] p-6 sm:p-8 shadow-[0_0_60px_-5px_rgba(14,165,233,0.45),0_30px_80px_-20px_rgba(2,6,23,0.9)] ring-1 ring-sky-400/20">
              <div
                aria-hidden
                className="pointer-events-none absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-sky-300 to-transparent"
              />

            <h2 className="text-xl font-semibold text-white">Vitajte v systéme Realnexa</h2>
            <p className="mt-1 text-sm text-slate-400">
              Zadajte svoje prihlasovacie údaje.
            </p>

            {!challenge && (
            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <div>
                <label
                  htmlFor="email"
                  className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400"
                >
                  E-mail
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  spellCheck={false}
                  autoCapitalize="none"
                  autoCorrect="off"
                  placeholder="user@realnexa.sk"
                  className="block w-full rounded-lg border border-white/10 bg-slate-900/60 px-3.5 py-2.5 text-sm text-white shadow-inner placeholder:text-slate-500 focus:border-sky-400/60 focus:outline-none focus:ring-2 focus:ring-sky-500/30"
                />
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400"
                >
                  Heslo
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    autoComplete="current-password"
                    placeholder="••••••••"
                    className="block w-full rounded-lg border border-white/10 bg-slate-900/60 px-3.5 py-2.5 pr-10 text-sm text-white shadow-inner placeholder:text-slate-500 focus:border-sky-400/60 focus:outline-none focus:ring-2 focus:ring-sky-500/30"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Skryť heslo' : 'Zobraziť heslo'}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-500 transition-colors hover:text-sky-300 focus:outline-none"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {error && (
                <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="group relative mt-2 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-sky-500 to-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-sky-900/40 transition-all hover:from-sky-400 hover:to-blue-500 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:ring-offset-2 focus:ring-offset-[#050b1f] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? 'Prihlasovanie…' : 'Prihlásiť sa'}
              </button>

              <div className="pt-1 text-center">
                <Link
                  to="/forgot-password"
                  className="text-sm text-slate-400 transition-colors hover:text-sky-300"
                >
                  Zabudli ste heslo?
                </Link>
              </div>
            </form>
            )}

            {challenge && (
              <form onSubmit={handleVerify} className="mt-6 space-y-4">
                <p className="text-sm text-slate-300">Dokončite prihlásenie druhým faktorom.</p>

                {challenge.methods.length > 1 && (
                  <div className="flex gap-2">
                    {challenge.methods.map((mItem) => (
                      <button key={mItem} type="button" onClick={() => switchMethod(mItem)}
                        className={`flex-1 rounded-lg border px-3 py-2 text-sm ${method === mItem ? 'border-sky-400 text-white' : 'border-white/10 text-slate-400'}`}>
                        {mItem === 'TOTP' ? 'Authenticator' : 'E-mail'}
                      </button>
                    ))}
                  </div>
                )}

                {method === 'EMAIL' && (
                  <button type="button" onClick={handleSendEmail} disabled={sendingEmail}
                    className="text-sm text-sky-300 hover:text-sky-200 disabled:opacity-60">
                    {sendingEmail ? 'Odosielam…' : emailSent ? 'Kód odoslaný — poslať znova' : 'Poslať kód na e-mail'}
                  </button>
                )}

                <div>
                  <label htmlFor="code" className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400">
                    {method === 'RECOVERY' ? 'Záložný kód' : 'Overovací kód'}
                  </label>
                  <input id="code" name="code" value={code} onChange={(e) => setCode(e.target.value)}
                    autoComplete="one-time-code" inputMode={method === 'RECOVERY' ? 'text' : 'numeric'}
                    placeholder={method === 'RECOVERY' ? 'XXXXX-XXXXX-XXXXX-XXXXX' : '000000'}
                    className="block w-full rounded-lg border border-white/10 bg-slate-900/60 px-3.5 py-2.5 text-sm text-white shadow-inner placeholder:text-slate-500 focus:border-sky-400/60 focus:outline-none focus:ring-2 focus:ring-sky-500/30" />
                </div>

                <label className="flex items-center gap-2 text-sm text-slate-300">
                  <input type="checkbox" checked={trustDevice} onChange={(e) => setTrustDevice(e.target.checked)} />
                  Dôverovať tomuto zariadeniu 30 dní
                </label>

                {error && (
                  <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={loading || !code.trim()}
                  className="group relative mt-2 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-sky-500 to-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-sky-900/40 transition-all hover:from-sky-400 hover:to-blue-500 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:ring-offset-2 focus:ring-offset-[#050b1f] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loading ? 'Overujem…' : 'Overiť a prihlásiť'}
                </button>

                {method === 'RECOVERY' ? (
                  <button type="button" onClick={() => switchMethod(challenge.defaultMethod)} className="block w-full text-center text-sm text-slate-400 hover:text-sky-300">
                    Späť na overovací kód
                  </button>
                ) : (
                  <button type="button" onClick={() => switchMethod('RECOVERY')} className="block w-full text-center text-sm text-slate-400 hover:text-sky-300">
                    Použiť záložný kód
                  </button>
                )}
              </form>
            )}
            </div>
          </div>

          <p className="mt-6 text-center text-xs text-slate-500">
            © {new Date().getFullYear()} Realnexa. Všetky práva vyhradené.{' '}
            <a
              href="https://files.realnexa.sk/realnexa-prod/legal/zasady-ochrany-osobnych-udajov.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-400 underline-offset-2 transition-colors hover:text-sky-300 hover:underline"
            >
              Zásady ochrany osobných údajov.
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}
